/**
 * meldungenText — die EINE Abbildung der zwei Seiten des Meldungsregisters
 * (`/declarations` und `/declarations/:id`) fuer die Woerter, die aus der API kommen.
 *
 * 🔴 WARUM DIESE DATEI EXISTIERT. `ablage.liste()` liefert `art` und `status` als
 *    ROHWERTE — der Slug `verpackg_jahresmengenmeldung` und das deutsche Wort
 *    `versandt` standen bis zum Regel-16-Gate so auf der Flaeche, mitten in einer
 *    englischen Oberflaeche. Die Listen-Nutzlast traegt KEINEN `bezeichnung` (er
 *    steht nicht in der SELECT-Liste, `backend/meldungen/ablage.py::liste`), die
 *    Fristen-Nutzlast hat einen — ohne diese Datei truege dieselbe Meldung auf
 *    EINER Seite zwei Namen. Deshalb liest auch der Fristenblock hier.
 *
 *    Der Hausbeleg fuer die Bauform ist `frontend/src/pages/monatsabschlussText.js`
 *    (dieselbe Lage: deutsche API-Statuswerte) — samt der Hausform fuer den
 *    unbekannten Fall: „Ein Status, den die Seite nicht kennt, zeigt sein rohes
 *    Wort — er verschwindet nicht."
 *
 * 🔴 Und seit dem 2026-10-16 stehen hier ZWEI weitere Rohwerte der API, aus
 *    demselben Grund: `dokument_typ` (`pdf` | `eml`) und `zahlen.gewichtslogik`
 *    (`geraetegewicht` | `mit_verpackung`). Keiner von beiden ist Kosmetik: der
 *    eine entscheidet, WIE das Dokument ausgeliefert wird (ein `eml` kann kein
 *    Browser im Rahmen zeigen), der andere ist der Riegel, an dem die zwei
 *    WEEE-Vorlagen haengen. Ein Literal auf der Flaeche waere in beiden Faellen
 *    eine Behauptung ueber eine Form, die die Seite nicht pruefen kann.
 *
 * 🔴 Die UI-WOERTER sind englisch (Owner-Regel: Woerter englisch, Formate deutsch).
 *    Die ZIFFERNFORMATE bleiben deutsch — `datumDe` liefert `15.1.2026`.
 *
 * Page-scoped (kein geteiltes Bauteil, Regel 12): die zwei Seiten lesen ihre
 * Woerter nur von hier, keine Zeile traegt ein eigenes Literal.
 *
 * 🔴 Status nie nur ueber Farbe (Regel 22 Nr. 2): `statusPill` liefert ein WORT.
 * 🔴 Unbekannt ist nie leer (Regel 22 Nr. 3): fehlt ein Wert, steht `KEIN_WERT`.
 */

/** Der Hausstrich fuer „kein Wert". */
export const KEIN_WERT = '—'

const humanize = (w) => String(w ?? '').replace(/_/g, ' ')

/**
 * Die Art einer Meldung als Wort.
 *
 * Quelle der Werte ist `backend/meldungen/ablage.py::ARTEN`
 * (`verpackg_jahresmengenmeldung`, `weee_monatsmeldung`); dieselbe Menge fuehrt
 * `backend/meldungen/pdf.py::TITEL` fuer den PDF-Titel. Ein unbekannter Wert
 * zeigt sein rohes Wort — er verschwindet nicht.
 */
export function artText(art) {
  switch (art) {
    case 'verpackg_jahresmengenmeldung': return 'VerpackG annual quantities declaration'
    case 'weee_monatsmeldung': return 'WEEE monthly declaration'
    default: return art ? humanize(art) : KEIN_WERT
  }
}

/**
 * Die FORM des Dokuments als Wort.
 *
 * Quelle der Werte ist `backend/meldungen/ablage.py::DOKUMENT_TYPEN`
 * (`pdf` | `eml`) — dieselbe Liste, gegen die der Schreibweg prueft und aus der
 * die Route Endung und Mimetype zieht. Ein unbekannter Wert zeigt sein rohes
 * Wort — er verschwindet nicht.
 *
 * 🔴 Warum das eine Abbildung braucht und kein Literal: bis 2026-10-16 nannte
 *    die Flaeche JEDE Datei „PDF", auch die textfoermige Fassung einer
 *    WEEE-Meldung (`dokument_typ = 'eml'`, Spec §4A). Das ist hier keine
 *    Kosmetik — das Wort ist der zugaengliche Name des Bauteils
 *    (`PdfInlineFrame title`) UND die Beschriftung des Auslieferungswegs.
 *
 * 🔴 Und die Form entscheidet ueber den WEG, nicht nur ueber das Wort: ein
 *    `eml` ist `message/rfc822`, das zeichnet kein Browser. Die Seite prueft
 *    das an `typ === 'pdf'` und nicht daran, ob der Rahmen es „irgendwie
 *    schafft" — siehe `MeldungDetail.jsx`, Dokument-Panel.
 */
export function dokumentText(typ) {
  switch (typ) {
    case 'pdf': return 'PDF'
    case 'eml': return 'Email (.eml)'
    default: return typ ? humanize(typ) : KEIN_WERT
  }
}

/**
 * Die GEWICHTSGRUNDLAGE einer WEEE-Meldung als Wort.
 *
 * Quelle ist `backend/weee_report.py` (`gewichtslogik` in `build_weee_report`)
 * — der Riegel, an dem die zwei Mailvorlagen haengen; die Registerform traegt
 * ihn als `zahlen.gewichtslogik` (Spec §4B).
 *
 * 🔴 Ohne diese Zeile stuende der ROHE deutsche Wert („geraetegewicht") mitten
 *    in der englischen Oberflaeche — dieselbe Sorte Leck, gegen die diese Datei
 *    ueberhaupt existiert.
 *
 * 🔴 Und er gehoert sichtbar auf die Flaeche, nicht in eine Fussnote: die zwei
 *    Werte bedeuten VERSCHIEDENE Kilogramm-Zahlen fuer dieselbe Meldung. Wer
 *    sie nicht sieht, liest eine Zahl ohne ihre Grundlage.
 */
export function gewichtslogikText(logik) {
  switch (logik) {
    case 'geraetegewicht': return 'device weight'
    case 'mit_verpackung': return 'incl. packaging'
    default: return logik ? humanize(logik) : KEIN_WERT
  }
}

/**
 * Status -> Pill-Text UND -Ton. EINE Abbildung fuer Liste UND Detail.
 *
 * 🔴 Vorher trug derselbe Status zwei Toene: `verworfen` war in der Liste
 *    `plain` und im Detail `warn`, und ein unbekannter Status ebenso. Wer von
 *    der Liste ins Detail klickte, sah dieselbe Meldung in einer anderen Farbe.
 *
 * 🔴 `verworfen` ist `plain`, nicht `warn`: `ablage.UEBERGAENGE` fuehrt es als
 *    ENDzustand (`verworfen -> ∅`) — eine bewusst geschlossene Meldung ist keine
 *    Warnung. `entwurf` ist der eine Zustand, der Handlung braucht (er ist der
 *    Grund, warum es die Fristen-Wache ueberhaupt gibt) und traegt deshalb `warn`.
 */
export function statusPill(status) {
  switch (status) {
    case 'versandt': return { text: 'Sent', tone: 'ok' }
    case 'bestaetigt': return { text: 'Confirmed', tone: 'ok' }
    case 'entwurf': return { text: 'Draft', tone: 'warn' }
    case 'verworfen': return { text: 'Discarded', tone: 'plain' }
    default:
      // Ein Status, den die Seite nicht kennt, zeigt sein rohes Wort.
      return { text: status ? humanize(status) : KEIN_WERT, tone: 'plain', unbekannt: true }
  }
}

/**
 * Ein Datum als `15.1.2026`; Leeres und Unlesbares → `KEIN_WERT`.
 *
 * 🔴 Eine KALENDERANGABE ist kein ZEITPUNKT — und die zwei Quellen dieser Seiten
 *    liefern beide Formen. `versandt_am` kommt als Zeitpunkt (`TIMESTAMPTZ`),
 *    `faellig_am` als reines Datum; Flasks JSON-Encoder schreibt ein `date` als
 *    HTTP-Datum um Mitternacht GMT („Sun, 15 Feb 2026 00:00:00 GMT", so in
 *    `backend/meldungen/wache.py::offene`; gemessen 2026-10-14 gegen Flask 2.3.3
 *    / werkzeug 3.1.3).
 *
 *    🔴 Deshalb KEIN `^\d{4}-\d{2}-\d{2}`-Muster wie in
 *    `monatsabschlussText.datumDe`: es faellt auf der HTTP-Form durch und zeigte
 *    `—` fuer eine Frist, die es gibt.
 *
 *    🔴 Und deshalb auch nicht einfach `new Date(wert)` fuer ALLES:
 *    `new Date('2027-02-15')` liest eine Kalenderangabe als UTC-Mitternacht, und
 *    `toLocaleDateString` rendert sie in jeder Zeitzone HINTER UTC dann als
 *    VORTAG — fuer einen Betrachter in Berlin folgenlos, fuer jeden anderen
 *    falsch. Beide Kalenderformen werden deshalb auf ihren TAG festgenagelt;
 *    nur ein echter Zeitpunkt bleibt die Ortszeit des Betrachters.
 */
export function datumDe(wert) {
  if (!wert) return KEIN_WERT
  const s = String(wert)
  // `T00:00:00` OHNE Zeitzone liest ECMAScript als ORTSZEIT — der Tag bleibt der
  // Tag, in jeder Zone. Unlesbares ergibt hier `Invalid Date` → KEIN_WERT.
  const isoTag = /^\d{4}-\d{2}-\d{2}$/.test(s)
  // Die HTTP-Form traegt ihr GMT selbst; sie wird deshalb in UTC gerendert.
  const httpTag = !isoTag && /^\w{3}, \d{2} \w{3} \d{4} 00:00:00 GMT$/.test(s)
  const d = isoTag ? new Date(`${s}T00:00:00`) : new Date(s)
  if (Number.isNaN(d.getTime())) return KEIN_WERT
  return httpTag ? d.toLocaleDateString('de-DE', { timeZone: 'UTC' })
                 : d.toLocaleDateString('de-DE')
}
